import React, { useMemo, useCallback, useState, useRef } from "react";
import DataTableWrapper from "../../../Components/CommonTableLayout/DataTableWrapper";
import { Link } from "react-router-dom";
import { fetchInboundOrders, fetchFilterOptions } from "../../../services/InboundOrders/InboundOrderTableServices";
import {
  getInitialFilterModel,
  createColumnFilterChangeHandler,
  createCascadingFilterParams,
  createDateFilterParams, createNumberFilterParams, sanitizeRootFilters
} from "../../../utils/columnFilterUtils";
import { Box } from "@mui/material";
import { formatValue } from "../../../utils/currencyUtils";
const DEFAULT_PAGE_SIZE = 25;
const FILTER_CACHE_KEY = 'supply_order';

const mapOrderTypeToApi = (orderType) => {
  if (!orderType) return "standardPO";

  switch (orderType) {
    case "standardPO":
      return "standardPO";
    case "stockTransferPO":
      return "stockTransferPO";
    default:
      return "standardPO";
  }
};

const buildColumnFiltersFromRaw = (rawFilterModel = {}) => {
  const result = {};

  Object.entries(rawFilterModel).forEach(([field, model]) => {
    if (!model) return;


    if (model.filterType === 'agSetColumnFilter') {
      result[field] = model.values || [];
      return;
    }

    if (model.filterType === 'agDateColumnFilter') {
      result[field] = model;
      return;
    }

    if (model.filterType === 'agNumberColumnFilter') {
      result[field] = model;
      return;
    }

    result[field] = model;
  });

  return result;
};


const InboundOrderTable = ({ filters, setRowCount, orderType, onColumnFilterChanged }) => {
  const [currentFilters, setCurrentFilters] = useState({});
  const calOrderType =
    orderType === "standardPO" ? "standardPO" : "stockTransferPO";
  const isStockTransfer = orderType === "stockTransferPO";
  // Get initial filter model from localStorage
  const initialFilterModel = useMemo(() => getInitialFilterModel(FILTER_CACHE_KEY), []);
  const fetchCacheRef = useRef(new Map());
  const debounceTimersRef = useRef({});
  const apiOrderType = useMemo(
    () => mapOrderTypeToApi(orderType),
    [orderType]
  );

 const mapFiltersForApi = useCallback(
  (columnFilters) => {
    const mapped = {
      appliedFilters: {},
      orderType: apiOrderType,
    };

    Object.entries(columnFilters || {}).forEach(([key, filterObj]) => {
      if (key === "state" || key === "exceptionType") return;
      if (!filterObj) return;

      // ✅ SET FILTER (array)
      if (Array.isArray(filterObj)) {
        mapped.appliedFilters[key] = {
          filterType: "set",
          values: filterObj,
        };
        return;
      }

      // ✅ OBJECT FILTER
      if (typeof filterObj === "object") {

        // 🔹 DATE FILTER
        if (filterObj.filterType === "date" || filterObj.dateFrom !== undefined) {
          mapped.appliedFilters[key] = {
            filterType: "date",
            type: filterObj.type,
            dateFrom: filterObj.dateFrom ?? null,
            dateTo: filterObj.dateTo ?? null,
          };
          return;
        }

        // 🔹 NUMBER FILTER (amount fix)
        if (filterObj.filterType === "number" || filterObj.filter !== undefined) {
          mapped.appliedFilters[key] = {
            filterType: "number",
            type: filterObj.type,
            filter: filterObj.filter ?? null,
            filterTo: filterObj.filterTo ?? null,
          };
          return;
        }

        // 🔹 SET FILTER (stored format)
        if (filterObj.filterType === "set") {
          mapped.appliedFilters[key] = {
            filterType: "set",
            values: filterObj.values || [],
          };
          return;
        }

        // 🔹 TEXT FALLBACK
        mapped.appliedFilters[key] = {
          filterType: "text",
          values: filterObj.values ?? filterObj,
        };
      }
    });

    if (filters?.states?.length) {
      mapped.selectedStates = filters.states;
    }

    if (filters?.exceptions?.length) {
      mapped.selectedExceptions = filters.exceptions;
    }

    return mapped;
  },
  [filters, apiOrderType]
);


  const fetchOptionsStable = useCallback((col, columnFilters) => {
    const apiPayload = mapFiltersForApi(columnFilters);
    const key = `${col}::${JSON.stringify(apiPayload)}`;
    if (fetchCacheRef.current.has(key)) {
      return fetchCacheRef.current.get(key);
    }

    let resolveFn, rejectFn;
    const p = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });
    fetchCacheRef.current.set(key, p);

    if (debounceTimersRef.current[key]) clearTimeout(debounceTimersRef.current[key]);
    debounceTimersRef.current[key] = setTimeout(async () => {
      try {
        const res = await fetchFilterOptions(col, apiPayload);
        resolveFn(res);
      } catch (err) {
        fetchCacheRef.current.delete(key);
        rejectFn(err);
      } finally {
        delete debounceTimersRef.current[key];
      }
    }, 120);

    return p;
  }, [mapFiltersForApi]);

  const handleColumnFilterChanged = useCallback((columnFilters, rawFilterModel) => {
    try {
      // Save enriched AG Grid model for UI restoration
      if (rawFilterModel) {
        const enrichedRaw = Object.fromEntries(
          Object.entries(rawFilterModel || {}).map(([field, model]) => {
            let filterType = model?.filterType;

            if (!filterType) {
              if (Array.isArray(model?.values)) {
                filterType = 'set';
              } else if (model?.type) {
                const dateTypes = new Set([
                  'equals',
                  'notEqual',
                  'lessThan',
                  'greaterThan',
                  'inRange',
                  'blank',
                  'notBlank',
                ]);

                const numberTypes = new Set([
                  'equals',
                  'notEqual',
                  'lessThan',
                  'lessThanOrEqual',
                  'greaterThan',
                  'greaterThanOrEqual',
                  'inRange',
                  'blank',
                  'notBlank',
                ]);

                if (model.filterType === 'agDateColumnFilter') {
                  filterType = 'date';
                } else if (model.filterType === 'agNumberColumnFilter') {
                  filterType = 'number';
                } else {
                  filterType = dateTypes.has(model.type)
                    ? 'date'
                    : numberTypes.has(model.type)
                      ? 'number'
                      : 'text';
                }
              } else {
                filterType = 'unknown';
              }
            }

            return [field, { ...model, filterType }];
          })
        );

        localStorage.setItem(FILTER_CACHE_KEY, JSON.stringify(enrichedRaw));
      }
    } catch (error) {
      console.error('Error saving filters:', error);
    }

    const safeColumnFilters = buildColumnFiltersFromRaw(rawFilterModel);

    setCurrentFilters(safeColumnFilters);

    if (typeof onColumnFilterChanged === 'function') {
      onColumnFilterChanged(safeColumnFilters);
    }

    try {
      const pageKey = FILTER_CACHE_KEY;

    
      const keys = new Set([
        ...Object.keys(columnFilters || {}),
        ...Object.keys(rawFilterModel || {}),
      ]);

      const simplifiedWithTypes = {};

      keys.forEach((field) => {
        const raw = rawFilterModel?.[field];
        const supplied = columnFilters?.[field];
        const hasSupplied = supplied !== undefined && !(typeof supplied === 'object' && Object.keys(supplied).length === 0);
        let values = hasSupplied ? supplied : undefined;
        let filterType;

        if (raw) {
          const ft = String(raw.filterType || '').toLowerCase();

          if (ft.includes('set') || Array.isArray(raw.values)) {
            filterType = 'set';
            values = raw.values || [];
          } else if (ft.includes('date') || raw.filterType === 'agDateColumnFilter') {
            filterType = 'date';
            values = {
              type: raw.type || undefined,
              dateFrom: raw.dateFrom ?? raw.date ?? raw.filter ?? null,
              dateTo: raw.dateTo ?? raw.filterTo ?? null,
            };
          } else if (ft.includes('number') || raw.filterType === 'agNumberColumnFilter' || raw.filter !== undefined || raw.filterTo !== undefined) {
            filterType = 'number';
            const numberFrom = raw.filter !== undefined && raw.filter !== null ? Number(raw.filter) : null;
            const numberTo = raw.filterTo !== undefined && raw.filterTo !== null ? Number(raw.filterTo) : null;
           values = {
              type: raw.type || undefined,
              filterType: raw.filterType || undefined,
              filter: numberFrom,
              filterTo: numberTo,
            };
          } else if (raw.values !== undefined) {
            filterType = Array.isArray(raw.values) ? 'set' : undefined;
            values = raw.values;
          }
        }

        // Fallbacks when no raw model present
        if (filterType === undefined) {
          if (Array.isArray(supplied)) {
            filterType = 'set';
          } else if (hasSupplied && typeof supplied === 'object') {
            if (supplied.dateFrom !== undefined || supplied.dateTo !== undefined) {
              filterType = 'date';
            } else if (supplied.numberFrom !== undefined || supplied.numberTo !== undefined) {
              filterType = 'number';
            } else {
              filterType = supplied.filterType || undefined;
            }
          } else {
            filterType = undefined;
          }
        }

        if (Array.isArray(values)) {
          simplifiedWithTypes[field] = { filterType, values };
        } else if (values && typeof values === 'object') {
          simplifiedWithTypes[field] = { filterType, ...values };
        } else {
          simplifiedWithTypes[field] = { filterType, values };
        }
      });

      localStorage.setItem(`columnFilters_${pageKey}`, JSON.stringify(simplifiedWithTypes));
      window.dispatchEvent(new CustomEvent(`columnFiltersChanged_${pageKey}`));
    } catch (err) {
      console.warn('Failed to persist/notify column filter change', err);
    }
  }, [onColumnFilterChanged]);

  const columns = useMemo(
    () => {
      const cols = [
        {
          field: "orderNo",
          headerName: "Order No",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'orderNo',
            currentFilters
          ),
          cellRenderer: (params) => {
            let borderColor = "transparent";
            switch (params.data?.exceptionType) {

              case "Past Due":
                borderColor = "#fbc02d";
                break;
              case "Late":
                borderColor = "#F40000";
                break;
              case "Unconfirmed":
                borderColor = "#bc6bd9";
                break;
              case "On Time":
                borderColor = "#268000";
                break;
              default:
                borderColor = "black";
            }
            return (
              <div
                style={{
                  borderLeft: `5px solid ${borderColor}`,
                  paddingLeft: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Link
                  to={`order-detail/${calOrderType}/${params.value}`}
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                  state={{
                    menu: "Supply",
                    label: "Purchase Orders",
                    outerLabel: "Purchase Orders Detail",
                  }}
                >
                  {params.value}
                </Link>
              </div>
            );
          }

        },
        {
          field: "customer",
          headerName: "Customer",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'customer', currentFilters)
        },
        {
          field: "createdBy",
          headerName: "Created By",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'createdBy', currentFilters)
        },
        {
          field: "supplier",
          headerName: "Supplier",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'supplier', currentFilters)
        },
        {
          field: "supplierName",
          headerName: "Supplier Name",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'supplierName', currentFilters)
        },
        {
          field: "shipTo",
          headerName: "Ship To",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'shipTo', currentFilters)
        },
        {
          field: "shipToDescription",
          headerName: "Ship To Description",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'shipToDescription', currentFilters)
        },
        !isStockTransfer && {
          field: "soldTo",
          headerName: "Sold To",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'soldTo', currentFilters)
        },
        // {
        //   field: "billTo",
        //   headerName: "Bill To",
        //   filter: 'agSetColumnFilter',
        //   filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'billTo', currentFilters)
        // },
        {
          field: "lob",
          headerName: "LOB",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'lob', currentFilters)
        },
        {
          field: "incoTerms",
          headerName: "Incoterms",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'incoTerms', currentFilters)
        },
        {
          field: "creationDate",
          headerName: "Creation Date",
          filter: 'agDateColumnFilter',
          filterParams: createDateFilterParams()
        },
        {
          field: "amount",
          headerName: "Amount",
          filter: 'agNumberColumnFilter',
          filterParams: createNumberFilterParams(),
          valueFormatter: (params) => {
            if (params.value == null) return "";
            return formatValue(params.value);
          }
        },
        {
          field: "currency",
          headerName: "Currency",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'currency', currentFilters)
        },
        {
          field: "processType",
          headerName: "Process Type",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'processType', currentFilters)
        },
        {
          field: "erpOrderType",
          headerName: "ERP Order Type",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'erpOrderType', currentFilters)
        },
        {
          field: "orderType",
          headerName: "Order Type",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'orderType', currentFilters)
        },
      ];
      return cols.filter(Boolean);
    },
    [currentFilters, fetchOptionsStable, calOrderType, isStockTransfer]
  );

  const fetchRows = useCallback(
    async ({ page, size, sort }) => {
      try {
        const apiOrderType = mapOrderTypeToApi(orderType);
        const validatedFilters = sanitizeRootFilters(filters, columns);
        const { rows, totalElements } = await fetchInboundOrders({
          page,
          size,
          sort,
          filters: validatedFilters || {},
          orderType: apiOrderType,
        });

        if (setRowCount) setRowCount(totalElements);
        return { rows, totalElements };
      } catch (err) {
        return { rows: [], totalElements: 0 };
      }
    },
    [filters, setRowCount, orderType, columns]
  );

  // const appliedFilters = useMemo(
  //   () => [
  //     {
  //       label: "Type A",
  //       value: "typeA",
  //       onRemove: () => console.log("remove Type A"),
  //     },
  //     {
  //       label: "Type B",
  //       value: "typeB",
  //       onRemove: () => console.log("remove Type B"),
  //     },
  //   ],
  //   []
  // );

  return (
    <Box sx={{
      height: "calc(100vh - 255px)",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
      boxSizing: "border-box",
      pb: 1
    }}>
      <DataTableWrapper
        title="Purchase Orders"
        columns={columns}
        filters={filters}
        height={500}
        fetchRows={fetchRows}
        rowCount={undefined}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        onColumnFilterChanged={handleColumnFilterChanged}
        initialFilterModel={initialFilterModel}
      />
    </Box>
  );
};

export default InboundOrderTable;
